import { User, Users, CheckCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { HelpTooltip } from '../../../components/ui/HelpTooltip';
import { useOnboardingStore } from '../../../store/onboarding.store';

interface MigrationStrategyStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function MigrationStrategyStep({ onNext, onBack }: MigrationStrategyStepProps) {
  const { billingData, setBillingData } = useOnboardingStore();
  const [selectedStrategy, setSelectedStrategy] = useState<'gradual' | 'full' | null>(
    billingData.rolloverSubscriptionSelection === 'user_selects' ? 'gradual' :
    billingData.rolloverSubscriptionSelection === 'invite_specific' ? 'full' : null
  );

  useEffect(() => {
    if (selectedStrategy) {
      setBillingData({
        rolloverType: 'rollover-self-managed',
        rolloverSubscriptionSelection: selectedStrategy === 'gradual' ? 'user_selects' : 'invite_specific',
      });
    }
  }, [selectedStrategy, setBillingData]);

  const handleContinue = () => {
    if (!selectedStrategy) return;
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Choose Your Migration Strategy</h2>
        <p className="text-[#D1D5DB] mt-2">
          That&apos;s ok RollOps makes the Transition Process Super Smooth.
        </p>
        <p className="text-[#D1D5DB] mt-4 text-lg font-semibold">
          You have two options for Switching to RollOps.
        </p>
      </div>

      <div className="space-y-4">
        {/* Gradual Migration Option */}
        <button
          onClick={() => setSelectedStrategy('gradual')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedStrategy === 'gradual'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <User size={48} className="text-rollops-accent" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#EBFFEE]">Option 1: Gradual Migration</h3>
                <HelpTooltip
                  title="Gradual Migration"
                  content="Gradual migration allows you to keep your existing payment provider while transitioning students to RollOps one at a time as their contracts renew. New students are immediately enrolled in RollOps billing. This minimizes disruption and gives you time to transition at your own pace."
                />
              </div>
              <p className="text-[#D1D5DB] text-sm mb-4">
                Keep your existing provider and move existing students over one at a time upon their renewal dates.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-500 text-xs">✓</span>
                  </div>
                  <span className="text-[#D1D5DB]">Keep existing provider</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-500 text-xs">✓</span>
                  </div>
                  <span className="text-[#D1D5DB]">Move existing students one at a time upon their renewal dates</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-500 text-xs">✓</span>
                  </div>
                  <span className="text-[#D1D5DB]">All new students immediately upon setup completion</span>
                </div>
              </div>
            </div>
            {selectedStrategy === 'gradual' && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <CheckCircle size={20} className="text-white" />
              </div>
            )}
          </div>
        </button>

        {/* Full Migration Option */}
        <button
          onClick={() => setSelectedStrategy('full')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedStrategy === 'full'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Users size={48} className="text-rollops-accent" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#EBFFEE]">Option 2: Full Migration</h3>
                <HelpTooltip
                  title="Full Migration"
                  content="Full migration moves all existing students to RollOps billing immediately, regardless of their contract renewal dates. This provides a clean, unified billing system from day one but may require coordinating with your existing payment provider to ensure smooth transitions."
                />
              </div>
              <p className="text-[#D1D5DB] text-sm mb-4">
                Migrate all students from the start.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-500 text-xs">✓</span>
                  </div>
                  <span className="text-[#D1D5DB]">Migrate all students immediately</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-500 text-xs">✓</span>
                  </div>
                  <span className="text-[#D1D5DB]">Complete transition to RollOps billing</span>
                </div>
              </div>
            </div>
            {selectedStrategy === 'full' && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <CheckCircle size={20} className="text-white" />
              </div>
            )}
          </div>
        </button>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-[#363232] text-[#EBFFEE] rounded-lg hover:bg-[#504A4A] transition-colors"
        >
          Go Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!selectedStrategy}
          className={`px-6 py-3 rounded-lg transition-colors ${
            selectedStrategy
              ? 'bg-[#9B1421] text-white hover:bg-[#C01F2D]'
              : 'bg-[#363232] text-[#D1D5DB] cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
